<template>
  <div class="wrapper" @mouseover="hover = true" @mouseleave="hover = false">
    <div ref="media" class="media" :class="classes" :style="styles">
      <template v-if="showMedia">
        <Transition :name="transitionName">
          <video
            v-if="isVideo"
            v-show="mediaLoaded"
            loop
            muted
            playsinline
            disableRemotePlayback
            preload="none"
            autoplay
            :poster="responsivePreviewImages"
            @loadeddata="onMediaLoaded"
          >
            <source
              v-for="source in responsiveSources"
              :key="source.url"
              :src="source.url"
              :type="source.mimeType"
            />
          </video>
          <picture v-else v-show="mediaLoaded">
            <source
              v-if="largeMedia"
              :srcset="largeMedia"
              media="(min-width: 821px)"
            />
            <source
              v-if="mediumMedia"
              :srcset="mediumMedia"
              media="(min-width: 429px)"
            />
            <source :srcset="smallMedia" media="(max-width: 428px)" />
            <img
              :src="images.smallMedia"
              :alt="images.altText"
              @load="onMediaLoaded"
            />
          </picture>
        </Transition>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: "Media",
  inject: ["mq"],
  props: {
    images: {
      type: Object,
      default: () => ({}),
    },
    hoverImages: {
      type: Object,
      default: () => ({}),
    },
    sources: {
      type: Array,
      default: () => [],
    },
    cover: {
      type: Boolean,
      default: false,
    },
    previewImages: {
      type: Array,
      default: () => [],
    },
    coverPosition: {
      type: String,
      default: "center center",
    },
    noTransition: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      observer: null,
      intersected: false,
      mediaLoaded: false,
      hover: false,
    };
  },
  computed: {
    classes() {
      return {
        "media--cover": this.cover,
      };
    },
    styles() {
      return {
        "--coverPosition": `${this.coverPosition}`,
        "--blurryMedia": `url(${this.blurryMedia})`,
      };
    },
    responsiveSources() {
      if (!this.sources?.length) return null;

      return this.sources.filter((source) => {
        if (!source.mq) {
          return true;
        }
        if (source.mq === "large") {
          return this.mq.mdPlus;
        }
        return !this.mq.mdPlus;
      });
    },
    responsivePreviewImages() {
      if (this.previewImages.length === 0) return null;
      const previewImages = this.previewImages?.filter((image) => {
        if (!image.mq) {
          return true;
        }
        if (image.mq === "large") {
          return this.mq.mdPlus;
        }
        return !this.mq.mdPlus;
      });
      return previewImages[0].url;
    },
    showMedia() {
      return this.intersected;
    },
    isVideo() {
      return this.responsiveSources?.length > 0;
    },
    transitionName() {
      return this.noTransition ? "" : "fade";
    },
    largeMedia() {
      return this.generateSrcset(
        this.images.largeMedia,
        this.images.largeMedia2x
      );
    },
    mediumMedia() {
      return this.generateSrcset(
        this.images.mediumMedia,
        this.images.mediumMedia2x
      );
    },
    smallMedia() {
      return this.generateSrcset(
        this.images.smallMedia,
        this.images.smallMedia2x
      );
    },
    blurryMedia() {
      return this.images?.blurry;
    },
  },
  mounted() {
    this.observer = new IntersectionObserver(
      this.handleIntersection,
      this.options
    );
    this.observer.observe(this.$refs.media);
  },
  beforeUnmount() {
    this.observer.disconnect();
  },
  methods: {
    generateSrcset(src, src2x) {
      return src2x ? `${src} 1x, ${src2x} 2x` : src;
    },
    onMediaLoaded() {
      this.mediaLoaded = true;
    },
    handleIntersection([entry]) {
      if (entry && entry.isIntersecting) {
        this.intersected = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.media {
  background-size: cover;
  background-position: center center;
  background-image: var(--blurryMedia);

  img,
  video {
    max-width: 100%;
    height: auto;
    display: block;
    background-size: cover;
    background-position: center center;
    margin: 0 auto;
  }
}

.media--cover {
  height: 100%;

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: var(--coverPosition);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
